import { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import Seo from "./Seo";
import { satsToBtc, fmtUsd } from "./utils";
import { PageContainer } from "./sharedStyleds";
import { getBtcPrice } from "./getBtcPrice";
import { routeMeta } from "./routeMeta";
import {
  getRecommendedFees,
  getMempoolStatus,
  getProjectedBlocks,
} from "./mempoolApi";

// A block holds about a million weight-adjusted bytes, which is what turns a
// mempool backlog into "how many blocks until this clears".
const VBYTES_PER_BLOCK = 1000000;

// Blocks arrive roughly every ten minutes, so the numbers below only move
// meaningfully on that scale. Polling faster would spend somebody else's free
// API on digits that have not changed.
const REFRESH_MS = 30000;
const PRICE_REFRESH_MS = 300000;

// Sizes are in virtual bytes, which is what fee rates are quoted against.
// Figures are the standard ones: 10.5 vB of overhead, 68 vB per native segwit
// input, 31 vB per output; the legacy row uses 148 vB inputs and 34 vB outputs.
const TX_SIZES = [
  { label: "Simple send", vbytes: 141, note: "1 input, 2 outputs, native segwit" },
  { label: "Two inputs", vbytes: 209, note: "2 inputs, 2 outputs, native segwit" },
  { label: "Legacy send", vbytes: 226, note: "1 input, 2 outputs, from a 1... address" },
  { label: "Consolidation", vbytes: 722, note: "10 inputs swept into 1 output" },
];

const FEE_BANDS = [
  { key: "fastestFee", label: "Next block", detail: "~10 min" },
  { key: "halfHourFee", label: "Half hour", detail: "~3 blocks" },
  { key: "hourFee", label: "Hour", detail: "~6 blocks" },
  { key: "economyFee", label: "No hurry", detail: "hours to days" },
];

const MempoolPage = PageContainer;

const Header = styled.h1`
  /* An h1 for semantics - no page had one - sized to the h2 it replaces, so
     nothing moves. terminal.css puts h1 at 30px, which would be a jump. */
  font-size: 1.5rem;
  line-height: 1.4em;
  /* the gap under the nav belongs to PageContainer, not to the heading */
  margin-top: 0;
  margin-bottom: 2px;
`;

const Intro = styled.p`
  margin-bottom: 1.2rem;
`;

const RefreshNote = styled.span`
  font-size: 0.75rem;
  color: #a3abba;
  vertical-align: middle;
  margin-left: 0.5rem;
`;

const SectionTitle = styled.h3`
  margin-bottom: 0.2rem;
  font-size: 1rem;
`;

const SectionNote = styled.p`
  font-size: 0.75rem;
  color: #a3abba;
  margin-bottom: 0.6rem;
  max-width: 70ch;
`;

const Section = styled.section`
  margin-bottom: 1.8rem;
`;

// four across, dropping to two rather than one so a card is never orphaned
// alone on the last row
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.6rem;

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const FeeCard = styled.div`
  min-width: 0;
  padding: 0.6rem 0.7rem;
  border: 1px solid ${({ $lead }) => ($lead ? "#62c4ff" : "#3f3f44")};
  border-radius: 3px;
`;

const CardLabel = styled.p`
  margin: 0;
  font-size: 0.7rem;
  color: #a3abba;
  display: flex;
  justify-content: space-between;
  gap: 0.4rem;

  /* on a narrow card these two must break onto separate lines rather than
     each wrapping internally and interleaving with the other */
  flex-wrap: wrap;

  span {
    white-space: nowrap;
  }
`;

const FeeRate = styled.p`
  margin: 0.15rem 0 0;
  font-size: 1.35rem;
  line-height: 1.2;
`;

const FeeUnit = styled.span`
  font-size: 0.75rem;
  color: #a3abba;
  margin-left: 0.25rem;
`;

const FeeCost = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.85rem;
  color: #e8e9ed;
`;

const StatCard = styled.div`
  min-width: 0;
`;

const StatLabel = styled.p`
  margin: 0;
  font-size: 0.7rem;
  color: #a3abba;
`;

const StatValue = styled.p`
  margin: 0;
  font-size: 0.95rem;
  overflow-wrap: anywhere;
`;

const StatSub = styled.span`
  font-size: 0.75rem;
  color: #a3abba;
  margin-left: 0.3rem;
`;

const SizeBar = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: #a3abba;
  margin-bottom: 0.6rem;
`;

const SizeButton = styled.button`
  padding: 0 0.45rem;
  font-size: 0.75rem;
  background-color: ${({ $active }) => ($active ? "#3f3f44" : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "#a3abba")};
  border: 1px solid #3f3f44;
  border-radius: 3px;
  cursor: pointer;
`;

// the queue can run to eight blocks, which will not fit on a phone
const Queue = styled.div`
  display: flex;
  gap: 0.6rem;
  overflow-x: auto;
  padding-bottom: 0.4rem;
`;

const QueueBlock = styled.div`
  flex: 0 0 auto;
  width: 8.5rem;
  padding: 0.5rem 0.6rem;
  border: 1px solid #3f3f44;
  border-top: 3px solid ${({ $next }) => ($next ? "#62c4ff" : "#3f3f44")};
  border-radius: 3px;
  font-size: 0.75rem;
`;

const QueueWhen = styled.p`
  margin: 0 0 0.3rem;
  color: ${({ $next }) => ($next ? "#62c4ff" : "#a3abba")};
`;

const QueueFee = styled.p`
  margin: 0;
  font-size: 1rem;
  color: #e8e9ed;
`;

const QueueDetail = styled.p`
  margin: 0.15rem 0 0;
  color: #a3abba;
`;

// Questions people actually type, answered on the page in words.
//
// This started as a plan to add FAQPage structured data. That markup is now
// pointless: Google restricted FAQ rich results to government and health sites
// in 2023 and retired them entirely on 7 May 2026, so the schema produces no
// rich result for anyone. What still helps is the answers being here as text -
// for a reader who has no idea what a mempool is, and because assistants that
// summarise pages cite prose, not JSON-LD.
//
// The answers use live figures where the page has them, which is the only real
// advantage this page has over an article written once in 2023.
const Faq = styled.section`
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px dotted #3f3f44;
  max-width: 75ch;
`;

const Question = styled.h4`
  margin: 1rem 0 0.2rem;
  font-size: 0.95rem;
`;

const Answer = styled.p`
  margin: 0;
  font-size: 0.88rem;
  color: #a3abba;
`;

const Unavailable = styled.p`
  font-size: 0.8rem;
  color: #a3abba;
`;

const fmtSats = (sats) =>
  sats.toLocaleString(undefined, { maximumFractionDigits: 0 });

// A backlog can be twenty minutes or two days, and "2,880 min" is not a figure
// anyone reads at a glance.
const fmtDuration = (minutes) => {
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = Math.round(minutes % 60);
    return rest ? `${hours}h ${rest}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days}d ${restHours}h` : `${days}d`;
};

// Fees run from cents to tens of dollars, and rounding a 4 cent fee to "$0.04"
// is right while rounding a $12.40 one to "$12" is not, so both keep cents.
const fmtFeeUsd = (usd) => (usd < 0.01 ? "<$0.01" : `$${fmtUsd(usd)}`);

const FEES_UNAVAILABLE =
  "Fee estimates are unavailable right now; the cards above fill in as soon as they load.";

// The price of a simple send, now and if you wait.
//
// Written as a function because the two figures are often the same: when the
// mempool is empty every band sits at the relay floor, and "costs about $0.11,
// or about $0.11 if you wait" reads like a bug. Saying so is both truer and
// more useful than quoting one number twice.
const faqCostAnswer = (fees, btcPrice) => {
  const SIMPLE_SEND_VB = 141;
  const price = (rate) =>
    btcPrice
      ? fmtFeeUsd(satsToBtc(rate * SIMPLE_SEND_VB) * btcPrice)
      : `${fmtSats(rate * SIMPLE_SEND_VB)} sats`;

  const now = `A straightforward payment — one input, two outputs — costs about ${price(
    fees.fastestFee
  )} to confirm in the next block, at ${fees.fastestFee} sat/vB.`;

  return fees.economyFee < fees.fastestFee
    ? `${now} Waiting an hour or two brings it down to about ${price(fees.economyFee)}.`
    : `${now} Every fee band is at the same rate at the moment, which happens when the mempool is close to empty — waiting would save nothing.`;
};

const Mempool = () => {
  const [fees, setFees] = useState(null);
  const [status, setStatus] = useState(null);
  const [projected, setProjected] = useState(null);
  const [btcPrice, setBtcPrice] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [sizeVb, setSizeVb] = useState(TX_SIZES[0].vbytes);
  // ticks once a second so "updated Ns ago" keeps counting up between refreshes
  const [, setClock] = useState(0);

  // the three fetches can still be in flight when the route unmounts; the
  // pattern matches the block tip lookup on Bitcoin Live
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    // in parallel: three independent endpoints, and one being slow should not
    // hold up the other two
    const [nextFees, nextStatus, nextProjected] = await Promise.all([
      getRecommendedFees(),
      getMempoolStatus(),
      getProjectedBlocks(),
    ]);
    // each stays as it was if this round failed, so a blip does not blank a
    // card that was showing a good number a moment ago
    if (!live.current) return;
    if (nextFees) setFees(nextFees);
    if (nextStatus) setStatus(nextStatus);
    if (nextProjected) setProjected(nextProjected);
    if (nextFees || nextStatus || nextProjected) setUpdatedAt(new Date());
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    const loadPrice = async () => {
      const price = await getBtcPrice();
      if (!cancelled && price) setBtcPrice(price);
    };
    loadPrice();
    const id = setInterval(loadPrice, PRICE_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setClock((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const costUsd = (satsPerVb) =>
    btcPrice ? satsToBtc(satsPerVb * sizeVb) * btcPrice : null;

  const secondsSince = updatedAt
    ? Math.max(0, Math.floor((Date.now() - updatedAt.getTime()) / 1000))
    : null;

  const blocksToClear = status ? status.vsize / VBYTES_PER_BLOCK : null;
  const waitingFeesBtc = status ? satsToBtc(status.total_fee) : null;
  const selectedSize = TX_SIZES.find((size) => size.vbytes === sizeVb);

  return (
    <MempoolPage>
      <Seo {...routeMeta["/mempool"]} path="/mempool" />
      <Header>
        {routeMeta["/mempool"].heading}
        {secondsSince !== null && (
          <RefreshNote>updated {secondsSince}s ago</RefreshNote>
        )}
      </Header>
      <Intro>{routeMeta["/mempool"].intro}</Intro>

      <Section>
        <SectionTitle>What a transaction costs</SectionTitle>
        <SizeBar>
          <span>Estimate for a</span>
          {TX_SIZES.map((size) => (
            <SizeButton
              key={size.label}
              type="button"
              $active={sizeVb === size.vbytes}
              onClick={() => setSizeVb(size.vbytes)}
            >
              {size.label}
            </SizeButton>
          ))}
        </SizeBar>

        {fees ? (
          <CardGrid>
            {FEE_BANDS.map((band) => {
              const rate = fees[band.key];
              const usd = costUsd(rate);
              return (
                <FeeCard key={band.key} $lead={band.key === "fastestFee"}>
                  <CardLabel>
                    <span>{band.label}</span>
                    <span>{band.detail}</span>
                  </CardLabel>
                  <FeeRate>
                    {rate}
                    <FeeUnit>sat/vB</FeeUnit>
                  </FeeRate>
                  <FeeCost>
                    {usd !== null
                      ? fmtFeeUsd(usd)
                      : `${fmtSats(rate * sizeVb)} sats`}
                  </FeeCost>
                </FeeCard>
              );
            })}
          </CardGrid>
        ) : (
          <Unavailable>
            Fee estimates are unavailable right now. They come from
            mempool.space, and this page will pick them up on the next refresh.
          </Unavailable>
        )}

        <SectionNote>
          Fees are paid per virtual byte, not per bitcoin sent, so what you move
          does not change the price — the shape of the transaction does. A{" "}
          {selectedSize?.label.toLowerCase()} is about{" "}
          {sizeVb.toLocaleString()} vB ({selectedSize?.note}).
          {fees && ` Nodes relay nothing under ${fees.minimumFee} sat/vB.`}
        </SectionNote>
      </Section>

      <Section>
        <SectionTitle>The backlog</SectionTitle>
        {status ? (
          <CardGrid>
            <StatCard>
              <StatLabel>Transactions waiting</StatLabel>
              <StatValue>{status.count.toLocaleString()}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Size of the queue</StatLabel>
              <StatValue>
                {(status.vsize / 1000000).toFixed(1)}
                <StatSub>vMB</StatSub>
              </StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Time to clear</StatLabel>
              <StatValue>
                {fmtDuration(blocksToClear * 10)}
                <StatSub>≈ {Math.round(blocksToClear)} blocks</StatSub>
              </StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Fees on the table</StatLabel>
              <StatValue>
                {waitingFeesBtc.toFixed(2)}
                <StatSub>BTC</StatSub>
              </StatValue>
            </StatCard>
          </CardGrid>
        ) : (
          <Unavailable>Mempool figures are unavailable right now.</Unavailable>
        )}
      </Section>

      <Section>
        <SectionTitle>The queue</SectionTitle>
        <SectionNote>
          What the next blocks would hold if they were mined this second, richest
          fees first. Pay above a block's median and you are competing for a spot
          in it.
        </SectionNote>
        {projected && projected.length > 0 ? (
          <Queue>
            {projected.map((block, index) => {
              const low = block.feeRange?.[0];
              const high = block.feeRange?.[block.feeRange.length - 1];
              return (
                <QueueBlock key={index} $next={index === 0}>
                  <QueueWhen $next={index === 0}>
                    {index === 0 ? "next block" : `~${(index + 1) * 10} min`}
                  </QueueWhen>
                  <QueueFee>
                    {block.medianFee.toFixed(1)}
                    <FeeUnit>sat/vB</FeeUnit>
                  </QueueFee>
                  <QueueDetail>
                    {Number.isFinite(low) && Number.isFinite(high)
                      ? `${low.toFixed(1)}–${high.toFixed(1)} range`
                      : "—"}
                  </QueueDetail>
                  <QueueDetail>
                    {block.nTx.toLocaleString()} txs
                  </QueueDetail>
                </QueueBlock>
              );
            })}
          </Queue>
        ) : (
          <Unavailable>The block queue is unavailable right now.</Unavailable>
        )}
      </Section>

      <Faq>
        <SectionTitle>Common questions</SectionTitle>

        <Question>How much does a Bitcoin transaction cost right now?</Question>
        <Answer>
          {fees ? faqCostAnswer(fees, btcPrice) : FEES_UNAVAILABLE}
        </Answer>

        <Question>How long will my transaction take to confirm?</Question>
        <Answer>
          {status
            ? `That depends on what you paid relative to everyone else waiting. There are ${status.count.toLocaleString()} transactions queued, about ${fmtDuration(
                (status.vsize / VBYTES_PER_BLOCK) * 10
              )} of work at one block every ten minutes. Pay above a block's median in the queue above and you are competing for a place in it.`
            : "That depends on what you paid relative to everyone else waiting — pay above a block's median in the queue above and you are competing for a place in it."}
        </Answer>

        {/* The answers that need no live data come from routeMeta, because the
            prerender step writes those same words into the served HTML. Two
            copies of this text would drift, and the rendered one wins - which is
            the bug that made titles worth centralising in the first place. */}
        {routeMeta["/mempool"].faq.map(({ question, answer }) => (
          <div key={question}>
            <Question>{question}</Question>
            <Answer>{answer}</Answer>
          </div>
        ))}


      </Faq>

      <SectionNote>
        Fee data from mempool.space, refreshed every {REFRESH_MS / 1000} seconds.
        Estimates are exactly that: a fee that clears the next block now can sit
        for hours if the backlog grows behind it.
      </SectionNote>
    </MempoolPage>
  );
};

export default Mempool;
